/**
 * Privacy-focused analytics service for tracking page views and events.
 * - No cookies (localStorage session only)
 * - Fingerprints are hashed
 * - Uses sendBeacon for reliable tracking during navigation
 */

const API_ENDPOINT = `${process.env.REACT_APP_API_URL}/api/t/`;
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const SESSION_KEY = 'analytics_session';
const SESSION_TIMESTAMP_KEY = 'analytics_session_ts';

interface SessionData {
  id: string;
  fingerprint: string;
}

interface TrackPayload {
  type: 'pageview' | 'event';
  session_id: string;
  fingerprint: string;
  path?: string;
  title?: string;
  referrer?: string;
  screen_width?: number;
  event_name?: string;
  event_category?: string;
  event_data?: Record<string, unknown>;
  event_value?: number;
  page_path?: string;
}

class AnalyticsService {
  private sessionId: string = '';
  private fingerprint: string = '';
  private initialized: boolean = false;
  private lastPath: string = '';

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    if (this.initialized) return;

    // Get or create session
    const session = this.getOrCreateSession();
    this.sessionId = session.id;
    this.fingerprint = session.fingerprint;
    this.initialized = true;

    // Track time on page when leaving
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.trackTimeOnPage();
      });
    }
  }

  private getOrCreateSession(): SessionData {
    const now = Date.now();

    // Check for existing session
    const existingSession = localStorage.getItem(SESSION_KEY);
    const existingTimestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY);

    if (existingSession && existingTimestamp) {
      const timestamp = parseInt(existingTimestamp, 10);
      if (now - timestamp < SESSION_TIMEOUT_MS) {
        // Session still valid, update timestamp
        localStorage.setItem(SESSION_TIMESTAMP_KEY, now.toString());
        return JSON.parse(existingSession);
      }
    }

    // Create new session
    const session: SessionData = {
      id: this.generateSessionId(),
      fingerprint: this.generateFingerprint(),
    };

    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    localStorage.setItem(SESSION_TIMESTAMP_KEY, now.toString());

    return session;
  }

  private generateSessionId(): string {
    // Generate a random session ID (UUID-like)
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
  }

  private generateFingerprint(): string {
    // Create a privacy-respecting fingerprint from stable browser characteristics
    // Note: This is hashed client-side for privacy
    const components: string[] = [
      navigator.userAgent,
      navigator.language,
      window.screen.width.toString(),
      window.screen.height.toString(),
      window.screen.colorDepth.toString(),
      new Date().getTimezoneOffset().toString(),
      navigator.hardwareConcurrency?.toString() || '',
      (navigator as Navigator & { deviceMemory?: number }).deviceMemory?.toString() || '',
    ];

    const str = components.join('|');
    return this.hashString(str);
  }

  private hashString(str: string): string {
    // Simple hash function (for fingerprint obfuscation)
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    // Convert to hex and pad to ensure consistent length
    const hex = Math.abs(hash).toString(16);
    return hex.padStart(16, '0');
  }

  private send(payload: TrackPayload): void {
    if (!this.initialized) {
      this.initialize();
    }

    // Update session timestamp
    localStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());

    const body = JSON.stringify(payload);

    // Try sendBeacon first (works during page unload)
    // Use text/plain to avoid CORS preflight
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'text/plain' });
      const sent = navigator.sendBeacon(API_ENDPOINT, blob);
      if (sent) return;
    }

    // Fallback to fetch (fire and forget)
    // Use text/plain to avoid CORS preflight for cross-origin requests
    fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
      },
      body,
      keepalive: true,
      credentials: 'omit',
      mode: 'cors',
    }).catch(() => {
      // Silently fail - analytics should not break the app
    });
  }

  /**
   * Track a page view
   */
  trackPageView(path: string, title?: string): void {
    // Avoid duplicate tracking of same path
    if (path === this.lastPath) return;
    this.lastPath = path;

    // Store page enter time for time-on-page calculation
    sessionStorage.setItem('analytics_page_enter', Date.now().toString());
    sessionStorage.setItem('analytics_page_path', path);

    const payload: TrackPayload = {
      type: 'pageview',
      session_id: this.sessionId,
      fingerprint: this.fingerprint,
      path,
      title: title || document.title,
      referrer: document.referrer || undefined,
      screen_width: window.innerWidth,
    };

    this.send(payload);
  }

  /**
   * Track a custom event
   */
  trackEvent(
    name: string,
    category: string,
    data?: Record<string, unknown>,
    value?: number
  ): void {
    const payload: TrackPayload = {
      type: 'event',
      session_id: this.sessionId,
      fingerprint: this.fingerprint,
      event_name: name,
      event_category: category,
      event_data: data,
      event_value: value,
      page_path: window.location.pathname,
    };

    this.send(payload);
  }

  /**
   * Track time spent on previous page (called on navigation)
   */
  private trackTimeOnPage(): void {
    const enterTime = sessionStorage.getItem('analytics_page_enter');
    const pagePath = sessionStorage.getItem('analytics_page_path');

    if (enterTime && pagePath) {
      const timeOnPage = Math.round((Date.now() - parseInt(enterTime, 10)) / 1000);
      if (timeOnPage > 0 && timeOnPage < 3600) {
        // Only track reasonable durations (< 1 hour)
        this.trackEvent('time_on_page', 'engagement', {
          path: pagePath,
          seconds: timeOnPage,
        });
      }
    }
  }

  /**
   * Helper method to reset session (useful for testing)
   */
  resetSession(): void {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(SESSION_TIMESTAMP_KEY);
    this.initialized = false;
    this.initialize();
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();

// Export class for testing
export { AnalyticsService };
